import { X, Target } from "lucide-react";
import { STAGE_LABELS, STAGE_OBJECTIVES, STAGE_DETAILED } from "../AchievementPanel";

export const EscalationGuideSlideout = ({
  isOpen,
  onClose,
  escalationState,
  matchFirstName,
  personalizeGuideText,
}) => {
  if (!isOpen) return null;

  const currentLevel = escalationState?.level || 1;
  const stageDetails = STAGE_DETAILED[currentLevel];

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Slideout Panel - RIGHT SIDE, FULL HEIGHT - Uses divine-gold via CSS variables */}
      <div className={`absolute right-0 top-0 bottom-0 w-full max-w-md transform transition-transform duration-300 ease-out ${
        isOpen ? "translate-x-0" : "translate-x-full"
      } bg-[#12121a] border-l border-divine-gold/30 shadow-2xl flex flex-col`}>
        
        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-4 border-b border-divine-gold/20 bg-[#12121a]/95 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-divine-gold/20">
                <Target className="w-5 h-5 text-divine-gold" />
              </div>
              <div>
                <h2 className="font-bold text-lg text-divine-gold">
                  ESCALATION GUIDE
                </h2>
                <p className="text-white/50 text-sm">Level {currentLevel}: {STAGE_LABELS[currentLevel]} Guide</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors hover:bg-divine-gold/20 text-divine-gold"
              data-testid="close-objective-slideout"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-6">
            {/* Current Objective Card */}
            <div className="p-4 rounded-xl border-2 bg-divine-gold/10 border-divine-gold/30">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🎯</span>
                <h3 className="font-bold text-divine-gold">
                  Your Objective
                </h3>
              </div>
              <p className="text-white/90 text-base leading-relaxed">
                {personalizeGuideText(STAGE_OBJECTIVES[currentLevel])}
              </p>
            </div>
            
            {/* XP Bonus Info Card */}
            <div className="p-4 rounded-xl border bg-emerald-500/10 border-emerald-500/30">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">⚡</span>
                <h3 className="font-semibold text-emerald-400">
                  XP Bonus for Co-Creative Responses
                </h3>
              </div>
              <p className="text-white/80 text-sm leading-relaxed mb-2">
                Earn <span className="font-bold text-emerald-400">bonus XP</span> when you collaborate with Agent Eros:
              </p>
              <ul className="text-white/70 text-sm space-y-1 list-disc list-inside">
                <li>✅ Type your own draft message</li>
                <li>✅ Edit or improve Agent Eros&apos;s suggestions</li>
                <li>⚠️ Copying suggestions verbatim = reduced XP</li>
              </ul>
            </div>
            
            {/* Stage Title */}
            {stageDetails && (
              <>
                <div className="text-center py-2 border-y border-divine-gold/20">
                  <h3 className="text-xl font-bold text-divine-gold">
                    {stageDetails.title}
                  </h3>
                </div>
                
                {/* Detailed Description */}
                <div className="space-y-4">
                  <div className="prose prose-invert prose-sm max-w-none">
                    {personalizeGuideText(stageDetails.description).split('\n\n').map((paragraph, idx) => {
                      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                        return (
                          <h4 key={idx} className="text-divine-gold font-bold text-base mt-6 mb-3">
                            {paragraph.replace(/\*\*/g, '')}
                          </h4>
                        );
                      }
                      if (paragraph.startsWith('**')) {
                        const [title, ...content] = paragraph.split('\n');
                        return (
                          <div key={idx} className="mb-4">
                            <h4 className="text-white font-semibold text-sm mb-2">
                              {title.replace(/\*\*/g, '')}
                            </h4>
                            <div className="text-white/70 text-sm space-y-1 pl-2 border-l-2 border-white/20">
                              {content.map((line, i) => (
                                <p key={i}>{personalizeGuideText(line)}</p>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <p key={idx} className="text-white/80 text-sm leading-relaxed mb-4">
                          {personalizeGuideText(paragraph)}
                        </p>
                      );
                    })}
                  </div>
                </div>
                
                {/* Examples Section */}
                <div className="border-t pt-6 border-divine-gold/20">
                  <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                    💬 Example Messages for {matchFirstName}
                  </h3>
                  <div className="space-y-3">
                    {stageDetails.examples.map((example, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-xl text-sm ${
                          example.good 
                            ? 'bg-green-500/10 border border-green-500/30' 
                            : 'bg-red-500/10 border border-red-500/30'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                            example.good 
                              ? 'bg-green-500/30 text-green-400' 
                              : 'bg-red-500/30 text-red-400'
                          }`}>
                            {example.good ? '✓ GOOD' : '✗ AVOID'}
                          </span>
                        </div>
                        <p className={`italic ${example.good ? 'text-green-200/90' : 'text-red-200/90'}`}>
                          &ldquo;{example.text}&rdquo;
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Footer - Fixed */}
        <div className="flex-shrink-0 p-4 border-t border-divine-gold/20 bg-[#12121a]/95 backdrop-blur-sm">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-semibold transition-colors bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30"
          >
            Close Guide
          </button>
        </div>
      </div>
    </div>
  );
};

export default EscalationGuideSlideout;
